/**
 * HR-portal FTUE — HR System steps (`/hris`).
 *
 * Owns the three on-page coachmarks of the `portal-hr-activation` tour that live
 * on `PortalHrisConnectionPage`:
 *
 *  - `hrisConnect` / `hrisWizardFinish` — a 2-step group sharing
 *    `PORTAL_HR_PHASE.hrisWizard` (chromes as "1 of 2" / "2 of 2", mirroring the
 *    Learn tour's empty-branch group in `use-learn-ftue`). Both only exist in
 *    the setup-wizard branch: `hrisConnect` anchors the "Connect HR system"
 *    button (wizard step 1); `hrisWizardFinish` anchors the "Finish" button that
 *    appears once a sync has run (wizard step 2, `step === 'done'`).
 *  - `hrisConnectedSync` — a single-concept step (own phase) anchored on the
 *    "Sync now" / "Check for departures" / "Reconnect" button row in
 *    `ConnectedPanel`, the wholly separate branch rendered once an org already
 *    has a connection.
 *
 * The wizard branch and the connected branch are mutually exclusive (two
 * different early-returns on the page), so at most one of the three steps is
 * ever a live candidate at a time.
 *
 * Registered from the page rather than the layout so each step's `condition`
 * can close over live page state: none of these buttons render while the page
 * is loading, for a non-entitled org, or (mutually) outside their own branch —
 * so the relevant step must not be a candidate, and must not block the rest of
 * the tour, in any other state.
 *
 * Part of the shared `portal-hr-activation` flow; `use-portal-hr-ftue` owns the
 * flow's dashboard + sidebar-nav steps and the auto-start logic. See `flow.ts`.
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_PHASE,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

const HRIS_PATH = '/hris'

/** This hook's own step ids, for the re-eval effect below. */
const OWN_STEP_IDS: readonly string[] = [
  PORTAL_HR_STEP.hrisConnect,
  PORTAL_HR_STEP.hrisWizardFinish,
  PORTAL_HR_STEP.hrisConnectedSync,
]

export interface PortalHrisFtueArgs {
  /**
   * Whether the "Connect HR system" button is currently on screen — true only
   * while `PortalHrisConnectionPage` renders its setup wizard (not loading, not
   * a non-entitled org, not an org that already has a connection). The page
   * derives this from the same expressions its early-returns test.
   */
  wizardVisible: boolean
  /**
   * Whether the wizard's step-2 "Finish" button is currently on screen — true
   * only once `wizardVisible` holds AND the wizard's own local state has
   * reached its "done" step with a sync result in hand. Mirrors the JSX gate
   * that renders the button (`s.id === 'done' && syncResult`); needed because
   * `wizardVisible` alone doesn't distinguish the wizard's two sub-steps.
   */
  wizardFinishVisible: boolean
  /**
   * Whether the "Sync now" / "Check for departures" / "Reconnect" button row is
   * currently on screen — true only while `PortalHrisConnectionPage` renders
   * `ConnectedPanel`. Mirrors `wizardVisible`'s guard clauses with the
   * connected-state early return's clause inverted rather than negated.
   */
  connectedVisible: boolean
}

export function usePortalHrisFtue({
  wizardVisible,
  wizardFinishVisible,
  connectedVisible,
}: PortalHrisFtueArgs) {
  const connectCtaAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.hrisConnectCta)
  const wizardFinishAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.hrisWizardFinishCta)
  const connectedActionsAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.hrisConnectedActions)

  // Live page state for the step `condition`s (registered once below, so the
  // closures must read the current value, not the mount-time snapshot).
  const wizardVisibleRef = useRef(wizardVisible)
  wizardVisibleRef.current = wizardVisible
  const wizardFinishVisibleRef = useRef(wizardFinishVisible)
  wizardFinishVisibleRef.current = wizardFinishVisible
  const connectedVisibleRef = useRef(connectedVisible)
  connectedVisibleRef.current = connectedVisible

  // Register the three on-page steps once. Registration is idempotent (keyed by
  // id) and does not itself trigger evaluation.
  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.hrisConnect,
        // Shared phase with hrisWizardFinish below: a 2-step group that only
        // exists in the setup-wizard branch, so it chromes as "1 of 2" (mirrors
        // the Learn tour's empty-branch group in use-learn-ftue). skipScope
        // stays the portalStep() default ('phase'), so skipping this pair
        // retires only the pair, not the (separately-phased) connected-sync
        // step.
        phase: PORTAL_HR_PHASE.hrisWizard,
        order: PORTAL_HR_ORDER.hrisConnect,
        trigger: { type: 'immediate' },
        // Not a candidate unless the connect button is actually rendered: on
        // `/hris`, in the setup-wizard state. Gating on the path as well means
        // this immediate step never blocks the rest of the flow while the admin
        // is on another page (see the note in `flow.ts`).
        condition: () => onPortalPath(HRIS_PATH) && wizardVisibleRef.current,
        anchorId: PORTAL_HR_ANCHOR.hrisConnectCta,
        placement: 'bottom',
        // The anchor is the single "Connect HR system" button; clicking it opens
        // the Merge Link modal (intent), so completing the step there is right.
        dismissOnAction: true,
        content: {
          body: 'Connect your HR system to sync people, org chart, and roles. No HR system? You can import a CSV instead.',
          dismissLabel: 'Ok',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.hrisWizardFinish,
        phase: PORTAL_HR_PHASE.hrisWizard,
        order: PORTAL_HR_ORDER.hrisWizardFinish,
        trigger: { type: 'immediate' },
        // Only a candidate once the wizard has actually reached its "done" step
        // (a sync has run) and the Finish button is on screen.
        condition: () => wizardVisibleRef.current && wizardFinishVisibleRef.current,
        anchorId: PORTAL_HR_ANCHOR.hrisWizardFinishCta,
        placement: 'bottom',
        // The anchor is the single "Finish" button; clicking it is the admin
        // acting on the intent (moving past the wait), so complete the step
        // there rather than leaving the coachmark hanging.
        dismissOnAction: true,
        content: {
          body: 'Your roster import can take up to 30 minutes. Click Finish now -- Pelgo keeps syncing in the background.',
          dismissLabel: 'Ok',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.hrisConnectedSync,
        phase: PORTAL_HR_STEP.hrisConnectedSync,
        order: PORTAL_HR_ORDER.hrisConnectedSync,
        trigger: { type: 'immediate' },
        // Not a candidate unless the connected-state button row is actually
        // rendered -- the wholly separate branch from the setup wizard above.
        condition: () => connectedVisibleRef.current,
        anchorId: PORTAL_HR_ANCHOR.hrisConnectedActions,
        placement: 'bottom',
        // Three different actions live in this row (Sync now / Check for
        // departures / Reconnect) -- no single click reads as "done" here, so
        // this is a plain "Ok" ack rather than dismissOnAction.
        content: {
          body: 'Sync now to pull the latest roster, or Check for departures to catch terminations early. Reconnect if the connection to your HR system breaks.',
          dismissLabel: 'Ok',
        },
      }),
    ])

    // Registration alone does not trigger evaluation — re-pick now the steps
    // exist. Idempotent; the flow is started/owned by `use-portal-hr-ftue`.
    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when any of this hook's page state changes (a plain re-render
  // doesn't re-run the engine). If one of this hook's own steps is active but
  // its `condition` has flipped false and it hasn't been acknowledged yet, mark
  // it seen so the tour advances cleanly instead of the coachmark just
  // vanishing. Otherwise just re-pick the candidate. Mirrors
  // `use-portal-exit-guide-ftue` / `use-learn-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: wizardVisible/wizardFinishVisible/connectedVisible intentionally re-trigger evaluation; the effect reads live state via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId && OWN_STEP_IDS.includes(activeStepId)) {
      const s = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (s?.condition && !s.condition() && !seen.has(activeStepId)) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [wizardVisible, wizardFinishVisible, connectedVisible])

  return {
    /** Ref callback for the "Connect HR system" `<Button>` in the setup wizard. */
    connectCtaAnchor,
    /** Ref callback for the wizard's "Finish" `<Button>` (step 2, `step === 'done'`). */
    wizardFinishAnchor,
    /** Ref callback for the connected-state button row (Sync now / Check for departures / Reconnect). */
    connectedActionsAnchor,
  }
}
